import React, { FC, ReactElement, ReactNode, useState } from 'react';

import { Icon } from '@iconify/react';
import { Popover, styled } from '@mui/material';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import FallbackLetterAvatar from '~/components/atoms/FallbackLetterAvatar';
import Logo from '~/components/atoms/Logo';
import ButtonSuggestion from '~/components/layouts/MainLayout/components/ButtonSuggestion';
import SidebarDesktop from '~/components/layouts/MainLayout/components/Sidebar/desktop';
import InnerNavigation from '~/components/molecules/InnerNavigation';
import { AvatarType } from '~/components/molecules/LogoMessageWrapper';
import SupportModal from '~/components/molecules/SupportModal';
import { useModalContext } from '~/contexts/modal/ModalContext';
import { SideBarItemType } from '~/core/sidebar';
import i18n from '~/i18n/config';
import { getLanguages } from '~/i18n/helpers';
import {
  PersonalInformationPath,
  SecurityPath,
  SubscriptionPath,
} from '~/modules/settings/constants';
import { SETTINGS_PROJECT_CONFIG } from '~/modules/settings/projects';
import projectConfig, { PROJECT } from '~/projects/config';
import analytics from '~/services/analytics';
import { useActions } from '~/store/hooks/useActions';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { checkIsSubUnlimited } from '~/utils/subscription';

import styles from './styles.module.scss';

const StyledPopover = styled(Popover)(() => ({
  '.MuiPopover-paper': {
    margin: 0,
  },
}));

type Props = {
  sidebarGroups: SideBarItemType[][];
  children?: React.ReactNode;
};

const DesktopSidebarHeader: FC<Props> = ({ children, sidebarGroups }) => {
  const { signOut, updateUserFields } = useActions();

  const { handleOpenModal } = useModalContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorElLanguage, setAnchorElLanguage] = useState<HTMLElement | null>(
    null,
  );

  const profile = useAppSelector((state) => state.profile);

  const { t } = useTranslation('general');

  const handleHelpClick = (): void => {
    analytics.trackEvent('navigation - open help section');

    handleOpenModal({
      onClose: () => analytics.trackEvent('navigation - close support modal'),
      maxWidth: '460px',
      component: ({ onClose }) => <SupportModal onClose={onClose} />,
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleLanguagesOpen = (event?: React.MouseEvent<HTMLElement>): void => {
    analytics.trackEvent('navigation - open language settings');

    if (event) {
      setAnchorElLanguage(event.currentTarget);
    }
  };

  const handleLanguagesClose = (): void => {
    setAnchorElLanguage(null);
  };

  const handleLanguageClick = async (lang: string): Promise<void> => {
    analytics.trackEvent('settings - chose language', {
      language: lang,
      previous_language: profile.platform_lang || i18n.instance.language || '',
    });

    handleLanguagesClose();
    handleMenuClose();

    if (profile.platform_lang !== lang) {
      i18n.instance.changeLanguage(lang);

      await updateUserFields({
        platform_lang: lang,
      });
    }
  };

  const settingsGroups: Pick<
    SideBarItemType,
    'key' | 'path' | 'getTitle' | 'Icon' | 'onClickCb'
  >[][] = [
    [
      {
        key: 'personal_information',
        path: PersonalInformationPath,
        getTitle: (t: TFunction): string =>
          SETTINGS_PROJECT_CONFIG.getPersonalInformationTitle(t),
        Icon: (props): ReactElement => (
          <Icon {...props} icon="material-symbols:person-outline" />
        ),
      },
      {
        key: 'security',
        path: SecurityPath,
        getTitle: (t: TFunction): string =>
          SETTINGS_PROJECT_CONFIG.getSecurityTitle(t),
        Icon: (props): ReactElement => (
          <Icon {...props} icon="material-symbols:key-outline" />
        ),
      },
      {
        key: 'subscription',
        path: SubscriptionPath,
        getTitle: (t: TFunction): string =>
          SETTINGS_PROJECT_CONFIG.getSubscriptionTitle(t),
        Icon: (props): ReactElement => (
          <Icon {...props} icon="material-symbols:card-membership-outline" />
        ),
      },
    ],
    [
      {
        key: 'language',
        path: '#',
        Icon: (props): ReactElement => (
          <Icon {...props} icon="material-symbols:translate-rounded" />
        ),
        getTitle: (t: TFunction): string =>
          t('general:app_language', 'App Language'),
        onClickCb: handleLanguagesOpen,
      },
    ],
    [
      {
        key: 'help',
        path: '#',
        Icon: (): ReactNode => <Icon icon="material-symbols:help-outline" />,
        getTitle: (t: TFunction): string => t('help', 'Help'),
        onClickCb: (): void => handleHelpClick(),
      },
      {
        key: 'logout',
        path: '#',
        Icon: (): ReactNode => <Icon icon="material-symbols:logout" />,
        getTitle: (t: TFunction): string => t('logout', 'Logout'),
        onClickCb: (): void => {
          signOut();
        },
      },
    ],
  ];

  const languagesGroups: Pick<
    SideBarItemType,
    'key' | 'path' | 'getTitle' | 'Icon' | 'onClickCb'
  >[][] = getLanguages().map((lang) => {
    return [
      {
        key: 'lang',
        path: '#',
        Icon: () =>
          profile.platform_lang === lang.value ? (
            <Icon
              icon="material-symbols:check-rounded"
              className={styles.check_icon}
            />
          ) : (
            <div className={styles.check_icon} />
          ),
        getTitle: lang.getText,
        onClickCb: () => handleLanguageClick(lang.value),
      },
    ];
  });

  return (
    <div className={styles.layout}>
      <div className={styles.layout__sidebar}>
        <SidebarDesktop items={sidebarGroups} />
      </div>

      <div className={styles.layout__main}>
        <div className={styles.header}>
          <div className={styles.header__logo_wrapper}>
            <div className={styles.header__logo}>
              <Logo />
            </div>

            {checkIsSubUnlimited(
              profile.is_stripe_user,
              profile.user_subscription.is_trial,
              profile.user_product.regularity,
            ) && (
              <div
                className={clsx(styles.header__unlimited_label, {
                  [styles['header__unlimited_label--sidekicker']]:
                    projectConfig.project === PROJECT.SIDEKICKER,
                })}
              >
                {t('unlimited', 'Unlimited')}
              </div>
            )}
          </div>

          <div className={styles.header__user}>
            <div className={styles.buttons_panel}>
              <ButtonSuggestion />
            </div>

            <div
              onClick={handleMenuOpen}
              className={clsx({
                [styles.contributor]: Boolean(
                  profile.extra_fields.is_contributor,
                ),
              })}
            >
              <FallbackLetterAvatar
                logo_src={profile.avatar}
                variant={AvatarType.USER}
                className={styles.header__avatar}
              >
                <span>{profile.name.charAt(0)}</span>
              </FallbackLetterAvatar>
            </div>
          </div>
        </div>

        <div className={styles.content}>{children}</div>
      </div>

      <StyledPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className={styles.header__nav_list}>
          <InnerNavigation groups={settingsGroups} />
        </div>
      </StyledPopover>

      <StyledPopover
        open={Boolean(anchorElLanguage)}
        anchorEl={anchorElLanguage}
        onClose={handleLanguagesClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div className={styles.header__nav_sub_list}>
          <InnerNavigation groups={languagesGroups} />
        </div>
      </StyledPopover>
    </div>
  );
};

export default DesktopSidebarHeader;
